/* Send-a-case form · RIGHT column.
 * Scan file dropzone, uploaded-file chips, traditional-impressions blurb,
 * optional purchase order #, lab-staff backlog checkbox, PDPPL consent,
 * and the submit / verify-panel switch. State comes from SendCaseContext.
 */
import { Upload, FileText, X, Loader2, ArrowRight } from "lucide-react";
import PrivacyConsentCheckbox from "@/components/PrivacyConsentCheckbox";
import { WorkflowField } from "./WorkflowField";
import { WorkflowVerifyPanel } from "./WorkflowVerifyPanel";
import { useSendCase } from "./SendCaseContext";

export function SendCaseFormRight() {
    const {
        files, drag, setDrag, onFiles, removeFile,
        poNumber, setPoNumber,
        isLabStaff, isBacklog, setIsBacklog,
        consent, setConsent,
        emailVerify, setEmailVerify, setPendingPayload,
        verifyCode, setVerifyCode,
        verifyError, setVerifyError,
        resendCooldown, submitting,
        submitWithCode, resendVerifyCode,
        batchSize = 0,
    } = useSendCase();
    // When the doctor has queued N additional cases via the batch panel,
    // the active form is case #(N+1) → the Submit button must reflect
    // submitting the whole batch in one click.
    const totalInBatch = batchSize + 1;
    const submitLabel = totalInBatch > 1 ? `Submit ${totalInBatch} cases` : "Send me a verification code";

    return (
        <div className="lg:col-span-5 space-y-6">
            <WorkflowField label="Scan files · STL / PLY / OBJ / DCM">
                <div
                    className={`tr-dropzone ${drag ? "dragging" : ""}`}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(e.dataTransfer.files); }}
                    data-testid="dropzone"
                >
                    <Upload size={22} className="mx-auto text-slate-400" />
                    <div className="mt-3 text-[14px] text-slate-700 font-medium">Drag &amp; drop scan files</div>
                    <div className="mt-1 text-[12.5px] text-slate-500">or</div>
                    <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-[13px] font-medium cursor-pointer transition">
                        Browse files
                        <input
                            data-testid="file-input"
                            type="file"
                            multiple
                            accept=".stl,.ply,.obj,.dcm,.zip"
                            className="hidden"
                            onChange={(e) => onFiles(e.target.files)}
                        />
                    </label>
                    <div className="mt-3 text-[11px] text-slate-400 font-mono-tr tracking-[0.18em] uppercase">Up to 8 files · 100 MB each</div>
                </div>
            </WorkflowField>

            {files.length > 0 && (
                <div className="space-y-2" data-testid="file-list">
                    {files.map((f, i) => (
                        <div key={`${f.name}-${f.size}-${i}`} className="flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg border border-slate-200 bg-slate-50/60">
                            <div className="flex items-center gap-2.5 min-w-0">
                                <FileText size={16} className="text-slate-500 shrink-0" />
                                <div className="min-w-0">
                                    <div className="text-[13px] font-medium text-slate-900 truncate">{f.name}</div>
                                    <div className="text-[11px] text-slate-500 font-mono-tr">{(f.size / 1024).toFixed(1)} KB</div>
                                </div>
                            </div>
                            <button type="button" onClick={() => removeFile(i)} className="text-slate-400 hover:text-rose-500" aria-label="Remove">
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">· Traditional impressions</div>
                <div className="text-[13.5px] text-slate-700 leading-relaxed">
                    Mailing PVS impressions? Ship to <span className="font-medium text-slate-900">Toothrocket Labs, Building 46, Street 989, Al Shafi Street, New Al Rayyan, Doha, Qatar</span>.
                    Include this online form&rsquo;s case number on the package.
                </div>
            </div>

            {/* PO number — AUTO-GENERATED for clinic / anonymous submitters
                (iter 56 spec: continuous integer sequence per tenant,
                continuing from the migrated max+1). The input is only shown
                to lab staff so they can override during backlog imports
                where the paper slip carries a pre-existing PO. Everyone
                else sees the auto value on the success panel. */}
            {isLabStaff && (
                <div data-testid="sendcase-po-wrap">
                    <label htmlFor="sendcase-po-number" className="block text-[10.5px] font-mono-tr tracking-[0.16em] uppercase text-slate-500 mb-1">
                        Purchase order number <span className="text-slate-400 normal-case font-sans tracking-normal">(lab-staff override — leave blank for auto)</span>
                    </label>
                    <input
                        id="sendcase-po-number"
                        type="text"
                        maxLength={80}
                        value={poNumber}
                        onChange={(e) => setPoNumber(e.target.value)}
                        placeholder="e.g. 28764 — or leave blank to auto-generate"
                        data-testid="sendcase-po-number"
                        className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 text-[13.5px] outline-none"
                    />
                </div>
            )}

            {isLabStaff && (
                <label data-testid="sendcase-backlog-checkbox-wrap"
                    className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 cursor-pointer hover:bg-amber-50 transition">
                    <input
                        type="checkbox"
                        checked={isBacklog}
                        onChange={(e) => setIsBacklog(e.target.checked)}
                        data-testid="sendcase-backlog-checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-700 focus:ring-amber-300"
                    />
                    <div className="text-[13px] leading-snug">
                        <div className="font-semibold text-amber-900">Mark as <em>backlog</em> case</div>
                        <div className="text-amber-800/85 mt-0.5">
                            Tick this if you&apos;re entering existing work that&apos;s already in progress (scanned offline, paper-trailed, or carried over from before the platform). Backlog cases <strong>skip auto-balance</strong> and don&apos;t count toward the late-cases KPI.
                        </div>
                    </div>
                </label>
            )}

            <PrivacyConsentCheckbox value={consent} onChange={setConsent} testid="sendcase-consent-pdppl" />

            {emailVerify ? (
                <WorkflowVerifyPanel
                    emailVerify={emailVerify}
                    verifyCode={verifyCode}
                    setVerifyCode={setVerifyCode}
                    verifyError={verifyError}
                    setVerifyError={setVerifyError}
                    resendCooldown={resendCooldown}
                    submitting={submitting}
                    onSubmit={submitWithCode}
                    onBack={() => { setEmailVerify(null); setPendingPayload(null); setVerifyCode(""); setVerifyError(null); }}
                    onResend={resendVerifyCode}
                />
            ) : (
                <button
                    type="submit"
                    data-testid="submit-case-btn"
                    disabled={submitting || !consent}
                    className="w-full tr-btn justify-center !py-4 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {submitting ? (
                        <>
                            <Loader2 size={18} className="animate-spin" /> {totalInBatch > 1 ? "Submitting cases…" : "Sending code…"}
                        </>
                    ) : (
                        <>
                            {submitLabel} <ArrowRight size={16} />
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
